/**
 * CSV modal: include columns + drag order for export.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, type FC } from 'react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import { Button } from '@/components/ui';

import type { CsvColumnSpec } from '../buildCsvColumns';
import { CsvExportSortableColumnRow } from './CsvExportSortableColumnRow';

interface CsvExportColumnsSectionProps {
  columns: CsvColumnSpec[];
  exportColumnKeys: string[];
  disabled: boolean;
  onKeysChange: (keys: string[]) => void;
}

export const CsvExportColumnsSection: FC<CsvExportColumnsSectionProps> = ({
  columns,
  exportColumnKeys,
  disabled,
  onKeysChange,
}) => {
  const specKeys = columns.map((c) => c.key);
  const labelByKey = useCallback(
    (k: string) => columns.find((c) => c.key === k)?.label ?? k,
    [columns],
  );

  const allSelected =
    specKeys.length > 0 &&
    exportColumnKeys.length === specKeys.length &&
    specKeys.every((k) => exportColumnKeys.includes(k));

  const toggleKey = (key: string) => {
    if (exportColumnKeys.includes(key)) {
      onKeysChange(exportColumnKeys.filter((k) => k !== key));
    } else {
      onKeysChange([...exportColumnKeys, key]);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = exportColumnKeys.indexOf(String(active.id));
    const newIndex = exportColumnKeys.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onKeysChange(arrayMove(exportColumnKeys, oldIndex, newIndex));
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Columns</h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
          disabled={disabled || specKeys.length === 0}
          onClick={() => onKeysChange(allSelected ? [] : [...specKeys])}
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </Button>
      </div>
      <div>
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">Include</p>
        <div className="flex max-h-28 flex-wrap gap-2 overflow-y-auto rounded-md border border-border/40 bg-muted/10 p-2">
          {columns.map((col) => (
            <label
              key={col.key}
              className="flex cursor-pointer items-center gap-1.5 rounded border border-border/50 bg-background px-2 py-1 text-xs hover:bg-muted/50"
            >
              <input
                type="checkbox"
                checked={exportColumnKeys.includes(col.key)}
                disabled={disabled}
                onChange={() => toggleKey(col.key)}
              />
              {col.label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">Column order in file</p>
        <p className="mb-2 text-[11px] text-muted-foreground/90">
          Drag chips to reorder; they wrap to new lines as needed—left-to-right, then top-to-bottom, matches CSV column order.
        </p>
        {exportColumnKeys.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/60 px-3 py-4 text-center text-xs text-muted-foreground">
            Select at least one column above.
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={exportColumnKeys} strategy={rectSortingStrategy}>
              <div role="list" className="flex flex-wrap gap-2 py-1">
                {exportColumnKeys.map((key) => (
                  <CsvExportSortableColumnRow
                    key={key}
                    id={key}
                    label={labelByKey(key)}
                    disabled={disabled}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </section>
  );
};
